import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Car, MapPin, Calendar, DollarSign, CheckCircle2, AlertCircle, Loader2, Tag, Heart, CreditCard, Building2, Copy, Route, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stripe promise - will be initialized after getting publishable key
let stripePromise = null;

const PaymentForm = ({ clientSecret, amount, rideId, token, tipAmount, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setError('');

    try {
      const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/pay/${token}/success`,
        },
        redirect: 'if_required'
      });

      if (paymentError) {
        setError(paymentError.message);
        setLoading(false);
        return;
      }

      if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Complete the payment on backend with tip amount
        const response = await fetch(`${API_URL}/api/pay/${token}/complete?payment_intent_id=${paymentIntent.id}&tip_amount=${tipAmount}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          onSuccess();
        } else {
          const data = await response.json();
          setError(data.detail || 'Failed to complete payment');
        }
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      <Button 
        type="submit" 
        className="w-full" 
        size="lg"
        disabled={!stripe || loading}
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Processing...
          </span>
        ) : (
          `Pay $${amount.toFixed(2)}`
        )}
      </Button>
    </form>
  );
};

export default function PaymentLinkPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rideDetails, setRideDetails] = useState(null);
  const [clientSecret, setClientSecret] = useState('');
  const [stripeKey, setStripeKey] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  // Payment method state
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' or 'zelle'
  const [zellePending, setZellePending] = useState(false);
  const [zelleSubmitting, setZelleSubmitting] = useState(false);
  
  // Zelle configuration
  const ZELLE_EMAIL = 'mmtainc28@gmail.com';
  const ZELLE_NAME = 'MMTA INC / Onyx Reserve.com';
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [finalAmount, setFinalAmount] = useState(null);
  
  // Tip state
  const [tipOption, setTipOption] = useState('none');
  const [customTip, setCustomTip] = useState('');
  const [tipAmount, setTipAmount] = useState(0);

  const TIP_OPTIONS = [
    { value: 'none', label: 'No Tip', amount: 0 },
    { value: '15', label: '15%', percent: 15 },
    { value: '20', label: '20%', percent: 20 },
    { value: '25', label: '25%', percent: 25 },
    { value: 'custom', label: 'Custom', amount: null }
  ];

  const calculateTipAmount = (option, baseAmount) => {
    if (option === 'none') return 0;
    if (option === 'custom') return parseFloat(customTip) || 0;
    const tipOpt = TIP_OPTIONS.find(t => t.value === option);
    if (tipOpt?.percent) {
      return (baseAmount * tipOpt.percent) / 100;
    }
    return 0;
  };

  // Update tip amount when tip option or custom tip changes
  useEffect(() => {
    const baseAmount = appliedCoupon ? appliedCoupon.new_total : (rideDetails?.amount || 0);
    const newTipAmount = calculateTipAmount(tipOption, baseAmount);
    setTipAmount(newTipAmount);
  }, [tipOption, customTip, rideDetails, appliedCoupon]);

  // Update final amount when tip changes
  useEffect(() => {
    if (rideDetails) {
      const baseAmount = appliedCoupon ? appliedCoupon.new_total : rideDetails.amount;
      setFinalAmount(baseAmount + tipAmount);
    }
  }, [tipAmount, rideDetails, appliedCoupon]);

  // Recreate payment intent when amount changes (including tip)
  const updatePaymentIntent = async (amount) => {
    if (!rideDetails) return;
    
    try {
      const intentResponse = await fetch(`${API_URL}/api/public/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          amount: amount,
          ride_details: {
            pickup_location: rideDetails.pickup_location,
            dropoff_location: rideDetails.dropoff_location,
            ride_id: rideDetails.ride_id,
            tip_amount: tipAmount
          }
        })
      });

      if (intentResponse.ok) {
        const intentData = await intentResponse.json();
        setClientSecret(intentData.client_secret);
      }
    } catch (err) {
      console.error('Failed to update payment intent:', err);
    }
  };

  // Update payment intent when final amount changes
  useEffect(() => {
    if (finalAmount && rideDetails && !paymentSuccess) {
      updatePaymentIntent(finalAmount);
    }
  }, [finalAmount]);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        // Get Stripe publishable key
        const configResponse = await fetch(`${API_URL}/api/stripe-config`);
        const configData = await configResponse.json();
        setStripeKey(configData.publishableKey);
        stripePromise = loadStripe(configData.publishableKey);

        // Get payment link details
        const response = await fetch(`${API_URL}/api/pay/${token}`);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Invalid payment link');
        }

        const data = await response.json();
        setRideDetails(data);
        setFinalAmount(data.amount);

        // Check if already paid
        if (data.payment_status === 'paid') {
          setPaymentSuccess(true);
          return;
        }

        // Create payment intent (using public endpoint - no auth required)
        const intentResponse = await fetch(`${API_URL}/api/public/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            amount: data.amount,
            ride_details: {
              pickup_location: data.pickup_location,
              dropoff_location: data.dropoff_location,
              ride_id: data.ride_id
            }
          })
        });

        if (intentResponse.ok) {
          const intentData = await intentResponse.json();
          setClientSecret(intentData.client_secret);
        }
      } catch (err) {
        setError(err.message || 'Failed to load payment details');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [token]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      const response = await fetch(`${API_URL}/api/public/coupons/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: couponCode.toUpperCase(),
          amount: rideDetails.amount
        })
      });

      const data = await response.json();

      if (response.ok) {
        setAppliedCoupon(data);
        setFinalAmount(data.new_total);
        
        // Create new payment intent with discounted amount
        const intentResponse = await fetch(`${API_URL}/api/public/create-payment-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            amount: data.new_total,
            ride_details: {
              pickup_location: rideDetails.pickup_location,
              dropoff_location: rideDetails.dropoff_location,
              ride_id: rideDetails.ride_id,
              coupon_code: data.code
            }
          })
        });

        if (intentResponse.ok) {
          const intentData = await intentResponse.json();
          setClientSecret(intentData.client_secret);
        }
      } else {
        setCouponError(data.detail || 'Invalid coupon code');
      }
    } catch (err) {
      setCouponError('Failed to validate coupon');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = async () => {
    setAppliedCoupon(null);
    setCouponCode('');
    setFinalAmount(rideDetails.amount);
    setCouponError('');

    // Re-create payment intent with original amount
    const intentResponse = await fetch(`${API_URL}/api/public/create-payment-intent`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        amount: rideDetails.amount,
        ride_details: {
          pickup_location: rideDetails.pickup_location,
          dropoff_location: rideDetails.dropoff_location,
          ride_id: rideDetails.ride_id
        }
      })
    });

    if (intentResponse.ok) {
      const intentData = await intentResponse.json();
      setClientSecret(intentData.client_secret);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="mt-2 text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Link Error</h2>
            <p className="text-gray-600">{error}</p>
            <Button 
              className="mt-6"
              onClick={() => navigate('/')}
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-green-700 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-4">
              Thank you for your payment. Your ride has been confirmed.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg text-left space-y-2">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Pickup</p>
                  <p className="font-medium">{rideDetails?.pickup_location}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-gray-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Dropoff</p>
                  <p className="font-medium">{rideDetails?.dropoff_location}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="h-4 w-4 text-gray-500 mt-1" />
                <div>
                  <p className="text-sm text-gray-500">Date & Time</p>
                  <p className="font-medium">{formatDate(rideDetails?.date)}</p>
                </div>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4">
              A confirmation email has been sent to {rideDetails?.client_email}
            </p>
            <Button 
              className="mt-6 w-full"
              onClick={() => navigate('/')}
            >
              Return to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
            <Car className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold">Onyx Reserve</h1>
          <p className="text-gray-600">Complete your ride payment</p>
        </div>

        {/* Ride Details */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Ride Details</CardTitle>
            <CardDescription>for {rideDetails?.client_name}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Multi-Trip Breakdown */}
            {rideDetails?.is_multi_trip && rideDetails?.trip_legs?.length > 0 ? (
              <div className="space-y-4">
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-purple-800 font-medium">
                    <Route className="h-4 w-4" />
                    Multi-Trip Order ({rideDetails.trip_legs.length} trips)
                  </div>
                </div>
                
                {rideDetails.trip_legs.map((leg, index) => (
                  <div key={index} className="border rounded-lg p-4 space-y-3 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="bg-purple-600 text-white text-xs px-2 py-1 rounded-full">
                        Trip {leg.trip_number}
                      </span>
                      <span className="text-sm text-gray-500">{leg.miles} miles</span>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-3 w-3 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Pickup</p>
                        <p className="text-sm font-medium">{leg.pickup}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="h-3 w-3 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Dropoff</p>
                        <p className="text-sm font-medium">{leg.dropoff}</p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Total Summary for Multi-Trip */}
                <div className="bg-gray-100 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span>Total Distance:</span>
                    <span className="font-medium">{rideDetails.miles_driven?.toFixed(1)} miles</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span>Est. Total Time:</span>
                    <span className="font-medium">{(rideDetails.time_hours * 60)?.toFixed(0)} min</span>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">{formatDate(rideDetails?.date)}</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Single Trip Display */
              <>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-4 w-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Pickup Location</p>
                    <p className="font-medium">{rideDetails?.pickup_location}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-4 w-4 text-red-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Dropoff Location</p>
                    <p className="font-medium">{rideDetails?.dropoff_location}</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">{formatDate(rideDetails?.date)}</p>
                  </div>
                </div>
              </>
            )}

            {/* Service Details / Notes Section */}
            {rideDetails?.notes && (
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="h-4 w-4 text-gray-500" />
                  <p className="text-sm font-medium text-gray-700">Service Details</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-sm whitespace-pre-wrap font-mono text-gray-700">
                  {rideDetails.notes}
                </div>
              </div>
            )}

            {/* Price Summary */}
            <div className="border-t pt-4 mt-4 space-y-2">
              {rideDetails?.discount_amount > 0 ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Ride Fare</span>
                    <span>${rideDetails.subtotal?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-green-600" data-testid="invoice-discount-row">
                    <span className="flex items-center gap-1">
                      <Tag className="h-4 w-4" />
                      Discount ({rideDetails.discount_percent}% off)
                    </span>
                    <span>-${rideDetails.discount_amount.toFixed(2)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between">
                  <span className="text-gray-600">Ride Fare</span>
                  <span>${rideDetails?.amount?.toFixed(2)}</span>
                </div>
              )}
              {appliedCoupon && (
                <div className="flex justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    <Tag className="h-4 w-4" />
                    Discount ({appliedCoupon.code})
                  </span>
                  <span>-${appliedCoupon.discount_amount.toFixed(2)}</span>
                </div>
              )}
              {tipAmount > 0 && (
                <div className="flex justify-between text-purple-600">
                  <span className="flex items-center gap-1">
                    <Heart className="h-4 w-4" />
                    Tip
                  </span>
                  <span>+${tipAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total</span>
                <span className="text-blue-600">${finalAmount?.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tip Section */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Heart className="h-4 w-4 text-purple-500" />
              Add a tip for your driver
            </CardTitle>
            <CardDescription>Show appreciation for great service</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-5 gap-2 mb-3">
              {TIP_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  type="button"
                  variant={tipOption === option.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => setTipOption(option.value)}
                  className={tipOption === option.value ? "bg-purple-600 hover:bg-purple-700" : ""}
                >
                  {option.label}
                </Button>
              ))}
            </div>
            {tipOption === 'custom' && (
              <div className="flex items-center gap-2">
                <span className="text-gray-500">$</span>
                <Input
                  type="number"
                  placeholder="Enter amount"
                  value={customTip}
                  onChange={(e) => setCustomTip(e.target.value)}
                  min="0"
                  step="1"
                  className="max-w-32"
                />
              </div>
            )}
            {tipAmount > 0 && (
              <p className="text-sm text-purple-600 mt-2">
                Tip amount: ${tipAmount.toFixed(2)}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Coupon Section */}
        <Card className="mb-6">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Have a coupon code?
            </CardTitle>
          </CardHeader>
          <CardContent>
            {appliedCoupon ? (
              <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-mono font-bold">{appliedCoupon.code}</span>
                  <span className="text-green-600">
                    {appliedCoupon.discount_type === 'percentage' 
                      ? `${appliedCoupon.discount_value}% off`
                      : `$${appliedCoupon.discount_value} off`}
                  </span>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={removeCoupon}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <Input
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="uppercase"
                />
                <Button 
                  variant="outline"
                  onClick={handleApplyCoupon}
                  disabled={couponLoading || !couponCode.trim()}
                >
                  {couponLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                </Button>
              </div>
            )}
            {couponError && (
              <p className="text-sm text-red-600 mt-2">{couponError}</p>
            )}
          </CardContent>
        </Card>

        {/* Payment Method Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Payment Method
            </CardTitle>
            <CardDescription>Choose how you'd like to pay</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Payment Method Tabs */}
            <div className="flex gap-2 mb-6">
              <Button
                type="button"
                variant={paymentMethod === 'card' ? 'default' : 'outline'}
                className={`flex-1 ${paymentMethod === 'card' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                onClick={() => setPaymentMethod('card')}
              >
                <CreditCard className="h-4 w-4 mr-2" />
                Credit/Debit Card
              </Button>
              <Button
                type="button"
                variant={paymentMethod === 'zelle' ? 'default' : 'outline'}
                className={`flex-1 ${paymentMethod === 'zelle' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                onClick={() => setPaymentMethod('zelle')}
              >
                <Building2 className="h-4 w-4 mr-2" />
                Zelle
              </Button>
            </div>

            {/* Card Payment Form */}
            {paymentMethod === 'card' && clientSecret && stripeKey && (
              <Elements 
                stripe={stripePromise} 
                options={{ 
                  clientSecret,
                  appearance: {
                    theme: 'stripe',
                    variables: {
                      colorPrimary: '#B08A57'
                    }
                  }
                }}
              >
                <PaymentForm 
                  clientSecret={clientSecret}
                  amount={finalAmount}
                  rideId={rideDetails?.ride_id}
                  token={token}
                  tipAmount={tipAmount}
                  onSuccess={() => setPaymentSuccess(true)}
                />
              </Elements>
            )}

            {/* Zelle Payment Section */}
            {paymentMethod === 'zelle' && (
              <div className="space-y-4">
                {zellePending ? (
                  <div className="bg-gold-50 border border-gold-200 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <Loader2 className="h-5 w-5 text-gold-600 animate-spin" />
                      <span className="font-medium text-gold-800">Payment Pending Verification</span>
                    </div>
                    <p className="text-sm text-gold-700">
                      Your Zelle payment is pending verification by our team. You will receive a confirmation email once verified.
                    </p>
                    <p className="text-sm text-gold-600 mt-2">
                      Reference: {rideDetails?.ride_id?.slice(0, 8).toUpperCase()}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <h4 className="font-medium text-purple-800 mb-3">Send Zelle Payment To:</h4>
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between p-2 bg-white rounded">
                          <div>
                            <p className="text-xs text-gray-500">Email</p>
                            <p className="font-mono font-medium">{ZELLE_EMAIL}</p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(ZELLE_EMAIL);
                              toast.success('Email copied!');
                            }}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        
                        <div className="flex items-center justify-between p-2 bg-white rounded">
                          <div>
                            <p className="text-xs text-gray-500">Business Name</p>
                            <p className="font-medium">{ZELLE_NAME}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t border-purple-200">
                        <p className="text-sm text-purple-700">
                          <strong>Amount to send:</strong> ${finalAmount?.toFixed(2)}
                        </p>
                        <p className="text-sm text-purple-600 mt-1">
                          <strong>Reference/Memo:</strong> {rideDetails?.ride_id?.slice(0, 8).toUpperCase()} - {rideDetails?.client_name}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 border rounded-lg p-4">
                      <h4 className="font-medium text-gray-800 mb-2">How to pay with Zelle:</h4>
                      <ol className="text-sm text-gray-600 space-y-2">
                        <li>1. Open your bank app and find Zelle</li>
                        <li>2. Send <strong>${finalAmount?.toFixed(2)}</strong> to <strong>{ZELLE_EMAIL}</strong></li>
                        <li>3. Include the reference in the memo: <strong>{rideDetails?.ride_id?.slice(0, 8).toUpperCase()}</strong></li>
                        <li>4. Click "I've Sent Payment" below after sending</li>
                      </ol>
                    </div>

                    <Button
                      type="button"
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      size="lg"
                      disabled={zelleSubmitting}
                      onClick={async () => {
                        setZelleSubmitting(true);
                        try {
                          const response = await fetch(`${API_URL}/api/pay/${token}/zelle-pending`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              amount: finalAmount,
                              tip_amount: tipAmount
                            })
                          });
                          
                          if (response.ok) {
                            setZellePending(true);
                            toast.success('Payment marked as pending. We will verify and confirm shortly.');
                          } else {
                            const data = await response.json();
                            toast.error(data.detail || 'Failed to submit payment');
                          }
                        } catch (err) {
                          toast.error('Failed to submit payment. Please try again.');
                        } finally {
                          setZelleSubmitting(false);
                        }
                      }}
                    >
                      {zelleSubmitting ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Submitting...
                        </span>
                      ) : (
                        "I've Sent the Zelle Payment"
                      )}
                    </Button>
                  </>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          {paymentMethod === 'card' ? 'Secure payment powered by Stripe' : 'Zelle payments are verified within 24 hours'}
        </p>
      </div>
    </div>
  );
}
